import {
  getColorPaletteColorVariableById,
  isColorPaletteColorId,
} from "@tokenterminal/core/feature/chart/utils/colors"
import { type ChartSerieColor } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { getComputedVariableColor } from "@tokenterminal/ui/Chart/colors"
import { useCallback, useMemo, useState } from "react"
import {
  type Color,
  ColorArea,
  ColorField,
  ColorSlider,
  ColorThumb,
  SliderTrack,
  parseColor,
  Input as AriaInput,
} from "react-aria-components"
import { ColorSwatch } from "./ColorSwatch"

const colorThumbClass =
  "border-2 border-solid border-white shadow-[0_0_0_1px_black,inset_0_0_0_1px_black] w-5 h-5 rounded-full box-sizing-border-box data-[focused]:border-black"

type ColorSelectorProps = {
  onChange: (color: ChartSerieColor) => void
  color: ChartSerieColor
}

export function ColorSelector({ color, onChange }: ColorSelectorProps) {
  const currentColor = useMemo(
    () =>
      isColorPaletteColorId(color)
        ? parseColor(
            getComputedVariableColor(getColorPaletteColorVariableById(color)!)
          )
        : parseColor(color),
    [color]
  )
  const [localColor, setLocalColor] = useState(currentColor)

  const onColorChange = useCallback(
    (c: Color) => {
      setLocalColor(c)
      onChange(c.toString("hex"))
    },
    [onChange]
  )

  return (
    <div className="flex flex-col gap-2 max-w-full">
      <ColorArea
        xChannel="saturation"
        yChannel="lightness"
        value={localColor.toString("hsl")}
        onChange={onColorChange}
        className="rounded-md"
        style={{ flexGrow: 1, aspectRatio: 1 }}
      >
        <ColorThumb className={colorThumbClass} />
      </ColorArea>
      <ColorSlider
        className="grid gap-1"
        channel="hue"
        value={localColor.toString("hsl")}
        onChange={onColorChange}
        style={{
          gridTemplateAreas: `"label output"
                              "track track"`,
          gridTemplateColumns: "1fr auto",
        }}
      >
        <SliderTrack
          className="rounded-md h-[28px]"
          style={{
            gridArea: "track",
          }}
        >
          <ColorThumb
            className={colorThumbClass}
            style={{
              top: "50%",
            }}
          />
        </SliderTrack>
      </ColorSlider>

      <div className="flex gap-2 items-center w-full">
        <ColorSwatch color={localColor.toString("hex")} />
        <ColorField
          aria-label="Custom color"
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.value.length < 7) {
              return
            }

            try {
              const color = parseColor(e.target.value)

              onColorChange(color)
            } catch (e) {}
          }}
          onChange={(color) => color && onColorChange(color)}
          value={localColor}
          className="flex-1"
        >
          <div className="tt-input-container w-full" data-size="small">
            <AriaInput className={"tt-input-base"} placeholder="Hex code" />
          </div>
        </ColorField>
      </div>
    </div>
  )
}
